import React from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Notes,
  CalendarToday,
} from "@mui/icons-material";
import { dateUtils } from "../utils/dateUtils";

function BookingForm({ bookingData, selectedSlot, lessonType, onChange }) {
  const handleInputChange = (field) => (event) => {
    onChange(field, event.target.value);
  };

  const handleCheckboxChange = (field) => (event) => {
    onChange(field, event.target.checked);
  };

  // Debug information (removed for production)

  if (!selectedSlot) {
    return (
      <Alert severity="error">
        Errore: nessuno slot selezionato. Torna al passaggio precedente e seleziona un orario.
      </Alert>
    );
  }

  // Create a fallback lessonType if not provided
  const effectiveLessonType = lessonType || {
    name: 'Lezione di Coaching',
    description: 'Sessione di coaching personalizzata',
    duration: selectedSlot.endTime && selectedSlot.startTime ?
      parseInt(selectedSlot.endTime.split(':')[0]) * 60 + parseInt(selectedSlot.endTime.split(':')[1]) -
      (parseInt(selectedSlot.startTime.split(':')[0]) * 60 + parseInt(selectedSlot.startTime.split(':')[1])) : 60,
    price: null
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        I tuoi Dati
      </Typography>

      {/* Elegant Selected Slot Summary */}
      <Paper
        id="booking-summary"
        elevation={3}
        sx={{
          mb: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          border: '2px solid',
          borderColor: 'primary.light',
          scrollMarginTop: '100px'
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: '#145444',
            color: 'white',
            p: 3,
            position: 'relative'
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            📋 Riepilogo Prenotazione
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Controlla i dettagli prima di procedere
          </Typography>
        </Box>

        {/* Content with improved readability */}
        <Box sx={{ p: 3, backgroundColor: '#efeae6' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CalendarToday color="primary" />
                  <Typography variant="h6" color="primary.main" fontWeight="bold">
                    Data e Orario
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{dateUtils.formatDate(selectedSlot.date, "EEEE, dd MMMM yyyy")}</strong>
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {selectedSlot.startTime} - {selectedSlot.endTime}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Durata: {dateUtils.calculateDuration(selectedSlot.startTime, selectedSlot.endTime)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#eaddd4',
                  border: '1px solid',
                  borderColor: 'success.main'
                }}
              >
                <Typography variant="h6" color="success.dark" fontWeight="bold" sx={{ mb: 2 }}>
                  {effectiveLessonType.name}
                </Typography>
                {effectiveLessonType.description && (
                  <Typography variant="body2" color="success.dark">
                    {effectiveLessonType.description}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Booking Form */}
      <Grid container spacing={3}>
        {/* Name Field */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nome e Cognome"
            value={bookingData.userName}
            onChange={handleInputChange("userName")}
            required
            InputProps={{
              startAdornment: <Person sx={{ mr: 1, color: "action.active" }} />,
            }}
            helperText="Il tuo nome completo"
          />
        </Grid>

        {/* Email Field */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Indirizzo Email"
            type="email"
            value={bookingData.userEmail}
            onChange={handleInputChange("userEmail")}
            required
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: "action.active" }} />,
            }}
            helperText="Riceverai la conferma a questo indirizzo"
          />
        </Grid>

        {/* Phone Field */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Numero di Telefono"
            type="tel"
            value={bookingData.userPhone}
            onChange={handleInputChange("userPhone")}
            required
            InputProps={{
              startAdornment: <Phone sx={{ mr: 1, color: "action.active" }} />,
            }}
            helperText="Formato: +39 123 456 7890 o 123 456 7890"
            placeholder="+39 123 456 7890"
          />
        </Grid>

        {/* Notes Field */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Note o Richieste Particolari"
            multiline
            rows={4}
            value={bookingData.notes}
            onChange={handleInputChange("notes")}
            InputProps={{
              startAdornment: (
                <Notes sx={{ mr: 1, color: "action.active", alignSelf: "flex-start", mt: 1 }} />
              ),
            }}
            helperText="Facoltativo: aggiungi eventuali note o richieste specifiche (max 500 caratteri)"
            inputProps={{ maxLength: 500 }}
          />
          {bookingData.notes && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
              {bookingData.notes.length}/500 caratteri
            </Typography>
          )}
        </Grid>

        {/* Carnet Checkbox */}
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={bookingData.hasCarnet || false}
                  onChange={handleCheckboxChange("hasCarnet")}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Ho già acquistato un pacchetto
                </Typography>
              }
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Privacy Notice */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mt: 3,
          backgroundColor: "grey.50",
          border: "1px solid",
          borderColor: "grey.300",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          <strong>Informativa Privacy:</strong> I tuoi dati personali saranno utilizzati esclusivamente
          per la gestione della prenotazione e per comunicazioni relative al servizio.
          Non saranno condivisi con terze parti senza il tuo consenso esplicito.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          <strong>Cancellazioni:</strong> Se hai bisogno di cancellare la tua prenotazione puoi farlo gratuitamente fino a 12 ore prima della lezione scrivendo a ilariastumpo.coaching@gmail.com. Per eventuali emergenze dell’ultimo minuto contattami al numero 3463721890.
        </Typography>
      </Paper>

      {/* Form Validation Messages - Only show after user has tried to input */}
      <Box mt={2}>
        {bookingData.userEmail && bookingData.userEmail.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.userEmail) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            L'indirizzo email non sembra valido
          </Alert>
        )}
        {bookingData.userPhone && bookingData.userPhone.length > 0 && !/^[+]?[\d\s\-\(\)]{8,}$/.test(bookingData.userPhone) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Il numero di telefono non sembra valido (deve contenere almeno 8 cifre)
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default BookingForm;